/**
 * Frontend-only sample data for Super Connector demo mode. None of this is
 * persisted — it is served locally by the api service when demo mode is on, so
 * non-Agent users can explore a realistic app without any backend access.
 *
 * The clients are deliberately concentrated in the Ang Mo Kio / Bishan /
 * Upper Thomson / Lentor / Toa Payoh corridor — the same north-central belt
 * where the hyping projects (The Orie, Lentor Central) sit — so the demo tells
 * a clear story: this agent owns one patch and is farming it hard.
 */
import type {
  Client, ClientTag, DashboardSummary, AgingData, HypingProjectOption,
  MessageTemplate, Campaign, AgentSettings, WhatsAppLiveStatus, WhatsAppCloudStatus,
  CalendlyEventType, LatestPickerSalesUpdate, EngagementTodaySummary,
  CampaignExecution, ClientEngagementProgress,
} from '../services/api';
import goodDayImage from './img/Have-a-Good-Day-Words.jpeg';

const now = Date.now();
const iso = (daysAgo: number) => new Date(now - daysAgo * 86_400_000).toISOString();

export const DEMO_TAGS: ClientTag[] = [
  { Id: 'd-tag-eoi', Name: 'EOI', Color: '#ed8936', ClientCount: 12 },
  { Id: 'd-tag-hyping', Name: 'Hyping Leads', Color: '#a855f7', ClientCount: 14 },
  { Id: 'd-tag-fb', Name: 'FB Leads', Color: '#3b82f6', ClientCount: 15 },
];

const tag = (id: string) => {
  const t = DEMO_TAGS.find((x) => x.Id === id)!;
  return { Id: t.Id, Name: t.Name, Color: t.Color ?? null };
};

export const DEMO_HYPING_PROJECTS: HypingProjectOption[] = [
  { projectId: 'd-proj-1', projectName: 'The Orie', mapLatitude: 1.3390, mapLongitude: 103.8470 },
  { projectId: 'd-proj-2', projectName: 'Parktown Residence', mapLatitude: 1.3520, mapLongitude: 103.9330 },
  { projectId: 'd-proj-3', projectName: 'Lentor Central Residences', mapLatitude: 1.3840, mapLongitude: 103.8360 },
];

const hp = (id: string) => {
  const p = DEMO_HYPING_PROJECTS.find((x) => x.projectId === id)!;
  return { id: p.projectId, name: p.projectName };
};

/**
 * A realistic spread across stages, tags and hyping projects — but with current
 * locations tightly clustered in the north-central corridor (Ang Mo Kio,
 * Bishan, Upper Thomson, Lentor, Toa Payoh, Serangoon) so the map reads as one
 * agent's active farming ground.
 */
export const DEMO_CLIENTS: Client[] = [
  {
    Id: 'd-c1', CEANumber: 'DEMO', Name: 'Sarah Tan', Mobile: '+65 9123 4567', Type: 'buyer',
    Budget: 1800000, PropertyType: 'Condo', PreferredLocation: 'Ang Mo Kio', Stage: 'new',
    Email: 'sarah.tan@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Ang Mo Kio Ave 8', CurrentLocationLat: 1.3691, CurrentLocationLng: 103.8454,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-eoi')], EngagementActive: true,
    CreatedAt: iso(1), UpdatedAt: iso(0),
  },
  {
    Id: 'd-c2', CEANumber: 'DEMO', Name: 'John Lim', Mobile: '+65 9234 5678', Type: 'buyer',
    Budget: 2500000, PropertyType: 'Condo', PreferredLocation: 'Bishan', Stage: 'contacted',
    Email: 'john.lim@example.com', HypingProjects: [hp('d-proj-2'), hp('d-proj-1')],
    CurrentLocation: 'Bishan Street 22', CurrentLocationLat: 1.3565, CurrentLocationLng: 103.8480,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-eoi'), tag('d-tag-hyping')], EngagementActive: true,
    CreatedAt: iso(4), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c3', CEANumber: 'DEMO', Name: 'Michelle Wong', Mobile: '+65 9345 6789', Type: 'investor',
    Budget: 3200000, PropertyType: 'Condo', PreferredLocation: 'Upper Thomson', Stage: 'viewing',
    Email: 'michelle.w@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Upper Thomson Road', CurrentLocationLat: 1.3545, CurrentLocationLng: 103.8325,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-hyping')], EngagementActive: false,
    CreatedAt: iso(9), UpdatedAt: iso(2),
  },
  {
    Id: 'd-c4', CEANumber: 'DEMO', Name: 'David Ng', Mobile: '+65 9456 7890', Type: 'buyer',
    Budget: 1500000, PropertyType: 'HDB', PreferredLocation: 'Toa Payoh', Stage: 'negotiation',
    Email: 'david.ng@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Toa Payoh Lorong 6', CurrentLocationLat: 1.3400, CurrentLocationLng: 103.8563,
    InterestedDistricts: '["D12"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-eoi'), tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(14), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c5', CEANumber: 'DEMO', Name: 'Priya Kumar', Mobile: '+65 9567 8901', Type: 'buyer',
    Budget: 2100000, PropertyType: 'Condo', PreferredLocation: 'Lentor', Stage: 'won',
    Email: 'priya.k@example.com', HypingProjects: [hp('d-proj-3'), hp('d-proj-2')],
    CurrentLocation: 'Lentor Central', CurrentLocationLat: 1.3840, CurrentLocationLng: 103.8360,
    InterestedDistricts: '["D26"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-fb')], EngagementActive: false,
    CreatedAt: iso(30), UpdatedAt: iso(3),
  },
  {
    Id: 'd-c6', CEANumber: 'DEMO', Name: 'Alex Chua', Mobile: '+65 9678 9012', Type: 'investor',
    Budget: 4000000, PropertyType: 'Condo', PreferredLocation: 'Bishan', Stage: 'lost',
    Email: 'alex.chua@example.com', HypingProjects: [hp('d-proj-2')],
    CurrentLocation: 'Marymount', CurrentLocationLat: 1.3486, CurrentLocationLng: 103.8397,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-hyping')], EngagementActive: false,
    CreatedAt: iso(45), UpdatedAt: iso(10),
  },
  {
    Id: 'd-c7', CEANumber: 'DEMO', Name: 'Grace Teo', Mobile: '+65 9789 0123', Type: 'buyer',
    Budget: 1650000, PropertyType: 'Condo', PreferredLocation: 'Ang Mo Kio', Stage: 'new',
    Email: 'grace.teo@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Ang Mo Kio Ave 3', CurrentLocationLat: 1.3625, CurrentLocationLng: 103.8490,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-eoi'), tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(2), UpdatedAt: iso(0),
  },
  {
    Id: 'd-c8', CEANumber: 'DEMO', Name: 'Ryan Goh', Mobile: '+65 9890 1234', Type: 'buyer',
    Budget: 2800000, PropertyType: 'Condo', PreferredLocation: 'Bishan', Stage: 'contacted',
    Email: 'ryan.goh@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Bishan Central', CurrentLocationLat: 1.3512, CurrentLocationLng: 103.8490,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-hyping')], EngagementActive: true,
    CreatedAt: iso(6), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c9', CEANumber: 'DEMO', Name: 'Nurul Aisyah', Mobile: '+65 9901 2345', Type: 'buyer',
    Budget: 1400000, PropertyType: 'HDB', PreferredLocation: 'Toa Payoh', Stage: 'viewing',
    Email: 'nurul.a@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Braddell Road', CurrentLocationLat: 1.3405, CurrentLocationLng: 103.8455,
    InterestedDistricts: '["D12"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-fb')], EngagementActive: false,
    CreatedAt: iso(11), UpdatedAt: iso(2),
  },
  {
    Id: 'd-c10', CEANumber: 'DEMO', Name: 'Kelvin Sim', Mobile: '+65 8012 3456', Type: 'investor',
    Budget: 3600000, PropertyType: 'Condo', PreferredLocation: 'Thomson', Stage: 'negotiation',
    Email: 'kelvin.sim@example.com', HypingProjects: [hp('d-proj-2'), hp('d-proj-3')],
    CurrentLocation: 'Sin Ming Avenue', CurrentLocationLat: 1.3610, CurrentLocationLng: 103.8360,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-hyping'), tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(18), UpdatedAt: iso(1),
  },
  // --- New stage ---
  {
    Id: 'd-c11', CEANumber: 'DEMO', Name: 'Daniel Ong', Mobile: '+65 8123 4560', Type: 'buyer',
    Budget: 1950000, PropertyType: 'Condo', PreferredLocation: 'Yio Chu Kang', Stage: 'new',
    Email: 'daniel.ong@example.com', HypingProjects: [hp('d-proj-2')],
    CurrentLocation: 'Yio Chu Kang Road', CurrentLocationLat: 1.3810, CurrentLocationLng: 103.8450,
    InterestedDistricts: '["D26"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-eoi'), tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(1), UpdatedAt: iso(0),
  },
  {
    Id: 'd-c12', CEANumber: 'DEMO', Name: 'Farhana Rahman', Mobile: '+65 8234 5601', Type: 'buyer',
    Budget: 1350000, PropertyType: 'HDB', PreferredLocation: 'Ang Mo Kio', Stage: 'new',
    Email: 'farhana.r@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Ang Mo Kio Ave 10', CurrentLocationLat: 1.3735, CurrentLocationLng: 103.8560,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-hyping')], EngagementActive: true,
    CreatedAt: iso(2), UpdatedAt: iso(0),
  },
  {
    Id: 'd-c13', CEANumber: 'DEMO', Name: 'Vignesh Raj', Mobile: '+65 8345 6012', Type: 'investor',
    Budget: 2650000, PropertyType: 'Condo', PreferredLocation: 'Thomson', Stage: 'new',
    Email: 'vignesh.raj@example.com', HypingProjects: [hp('d-proj-1'), hp('d-proj-3')],
    CurrentLocation: 'Thomson Plaza', CurrentLocationLat: 1.3555, CurrentLocationLng: 103.8330,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(3), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c14', CEANumber: 'DEMO', Name: 'Cheryl Lim', Mobile: '+65 8456 0123', Type: 'buyer',
    Budget: 2200000, PropertyType: 'Condo', PreferredLocation: 'Bishan', Stage: 'new',
    Email: 'cheryl.lim@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Bishan Street 13', CurrentLocationLat: 1.3540, CurrentLocationLng: 103.8420,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-eoi')], EngagementActive: false,
    CreatedAt: iso(3), UpdatedAt: iso(2),
  },
  // --- Contacted / Responded stage ---
  {
    Id: 'd-c15', CEANumber: 'DEMO', Name: 'Marcus Tan', Mobile: '+65 8567 0234', Type: 'investor',
    Budget: 3100000, PropertyType: 'Condo', PreferredLocation: 'Toa Payoh', Stage: 'contacted',
    Email: 'marcus.tan@example.com', HypingProjects: [hp('d-proj-2'), hp('d-proj-1')],
    CurrentLocation: 'Toa Payoh Central', CurrentLocationLat: 1.3350, CurrentLocationLng: 103.8500,
    InterestedDistricts: '["D12"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-hyping'), tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(5), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c16', CEANumber: 'DEMO', Name: 'Aishah Ismail', Mobile: '+65 8678 0345', Type: 'buyer',
    Budget: 1500000, PropertyType: 'HDB', PreferredLocation: 'Ang Mo Kio', Stage: 'contacted',
    Email: 'aishah.i@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Bright Hill Drive', CurrentLocationLat: 1.3625, CurrentLocationLng: 103.8330,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-eoi')], EngagementActive: true,
    CreatedAt: iso(6), UpdatedAt: iso(2),
  },
  {
    Id: 'd-c17', CEANumber: 'DEMO', Name: 'Benjamin Koh', Mobile: '+65 8789 0456', Type: 'buyer',
    Budget: 2400000, PropertyType: 'Condo', PreferredLocation: 'Ang Mo Kio', Stage: 'contacted',
    Email: 'benjamin.koh@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Ang Mo Kio Ave 1', CurrentLocationLat: 1.3660, CurrentLocationLng: 103.8410,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(7), UpdatedAt: iso(2),
  },
  {
    Id: 'd-c18', CEANumber: 'DEMO', Name: 'Divya Nair', Mobile: '+65 8890 0567', Type: 'investor',
    Budget: 4200000, PropertyType: 'Landed', PreferredLocation: 'Springleaf', Stage: 'contacted',
    Email: 'divya.nair@example.com', HypingProjects: [hp('d-proj-2')],
    CurrentLocation: 'Springleaf', CurrentLocationLat: 1.3960, CurrentLocationLng: 103.8195,
    InterestedDistricts: '["D26"]', DistanceFromPreferredLocation: 3,
    Tags: [tag('d-tag-hyping')], EngagementActive: false,
    CreatedAt: iso(8), UpdatedAt: iso(3),
  },
  // --- Viewing stage ---
  {
    Id: 'd-c19', CEANumber: 'DEMO', Name: 'Wei Jie Lee', Mobile: '+65 8901 0678', Type: 'buyer',
    Budget: 1750000, PropertyType: 'Condo', PreferredLocation: 'Bishan', Stage: 'viewing',
    Email: 'weijie.lee@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Shunfu Road', CurrentLocationLat: 1.3530, CurrentLocationLng: 103.8380,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-hyping')], EngagementActive: true,
    CreatedAt: iso(9), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c20', CEANumber: 'DEMO', Name: 'Samantha Ng', Mobile: '+65 8012 0789', Type: 'buyer',
    Budget: 2900000, PropertyType: 'Condo', PreferredLocation: 'Bishan', Stage: 'viewing',
    Email: 'samantha.ng@example.com', HypingProjects: [hp('d-proj-1'), hp('d-proj-2')],
    CurrentLocation: 'Bishan Street 24', CurrentLocationLat: 1.3585, CurrentLocationLng: 103.8520,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-fb'), tag('d-tag-eoi')], EngagementActive: true,
    CreatedAt: iso(10), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c21', CEANumber: 'DEMO', Name: 'Hafiz Osman', Mobile: '+65 8123 0890', Type: 'buyer',
    Budget: 1600000, PropertyType: 'HDB', PreferredLocation: 'Serangoon', Stage: 'viewing',
    Email: 'hafiz.osman@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Serangoon Central', CurrentLocationLat: 1.3496, CurrentLocationLng: 103.8730,
    InterestedDistricts: '["D19"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-hyping'), tag('d-tag-fb')], EngagementActive: false,
    CreatedAt: iso(11), UpdatedAt: iso(2),
  },
  {
    Id: 'd-c22', CEANumber: 'DEMO', Name: 'Rachel Chua', Mobile: '+65 8234 0901', Type: 'investor',
    Budget: 3400000, PropertyType: 'Condo', PreferredLocation: 'Lentor', Stage: 'viewing',
    Email: 'rachel.chua@example.com', HypingProjects: [hp('d-proj-2')],
    CurrentLocation: 'Lentor Green', CurrentLocationLat: 1.3865, CurrentLocationLng: 103.8390,
    InterestedDistricts: '["D26"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-eoi')], EngagementActive: true,
    CreatedAt: iso(12), UpdatedAt: iso(2),
  },
  // --- Negotiation stage ---
  {
    Id: 'd-c23', CEANumber: 'DEMO', Name: 'Jonathan Yeo', Mobile: '+65 8345 1012', Type: 'buyer',
    Budget: 2050000, PropertyType: 'Condo', PreferredLocation: 'Toa Payoh', Stage: 'negotiation',
    Email: 'jonathan.yeo@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Toa Payoh Lorong 1', CurrentLocationLat: 1.3380, CurrentLocationLng: 103.8460,
    InterestedDistricts: '["D12"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-fb')], EngagementActive: true,
    CreatedAt: iso(15), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c24', CEANumber: 'DEMO', Name: 'Meera Pillai', Mobile: '+65 8456 1123', Type: 'investor',
    Budget: 4600000, PropertyType: 'Condo', PreferredLocation: 'Bishan', Stage: 'negotiation',
    Email: 'meera.pillai@example.com', HypingProjects: [hp('d-proj-2'), hp('d-proj-3')],
    CurrentLocation: 'Bishan Street 11', CurrentLocationLat: 1.3520, CurrentLocationLng: 103.8460,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-hyping'), tag('d-tag-eoi')], EngagementActive: true,
    CreatedAt: iso(16), UpdatedAt: iso(1),
  },
  {
    Id: 'd-c25', CEANumber: 'DEMO', Name: 'Terence Low', Mobile: '+65 8567 1234', Type: 'buyer',
    Budget: 2750000, PropertyType: 'Condo', PreferredLocation: 'Ang Mo Kio', Stage: 'negotiation',
    Email: 'terence.low@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Ang Mo Kio Ave 6', CurrentLocationLat: 1.3705, CurrentLocationLng: 103.8530,
    InterestedDistricts: '["D20"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-fb'), tag('d-tag-hyping')], EngagementActive: true,
    CreatedAt: iso(17), UpdatedAt: iso(2),
  },
  // --- Won stage ---
  {
    Id: 'd-c26', CEANumber: 'DEMO', Name: 'Zoe Tan', Mobile: '+65 8678 1345', Type: 'buyer',
    Budget: 1900000, PropertyType: 'Condo', PreferredLocation: 'Yio Chu Kang', Stage: 'won',
    Email: 'zoe.tan@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Yio Chu Kang Ave', CurrentLocationLat: 1.3790, CurrentLocationLng: 103.8420,
    InterestedDistricts: '["D26"]', DistanceFromPreferredLocation: 1,
    Tags: [tag('d-tag-eoi')], EngagementActive: false,
    CreatedAt: iso(28), UpdatedAt: iso(4),
  },
  {
    Id: 'd-c27', CEANumber: 'DEMO', Name: 'Faizal Karim', Mobile: '+65 8789 1456', Type: 'investor',
    Budget: 3300000, PropertyType: 'Condo', PreferredLocation: 'Serangoon', Stage: 'won',
    Email: 'faizal.karim@example.com', HypingProjects: [hp('d-proj-1'), hp('d-proj-2')],
    CurrentLocation: 'Serangoon Garden', CurrentLocationLat: 1.3600, CurrentLocationLng: 103.8650,
    InterestedDistricts: '["D19"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-fb')], EngagementActive: false,
    CreatedAt: iso(33), UpdatedAt: iso(5),
  },
  {
    Id: 'd-c28', CEANumber: 'DEMO', Name: 'Natalie Goh', Mobile: '+65 8890 1567', Type: 'buyer',
    Budget: 2600000, PropertyType: 'Condo', PreferredLocation: 'Bidadari', Stage: 'won',
    Email: 'natalie.goh@example.com', HypingProjects: [hp('d-proj-2')],
    CurrentLocation: 'Bidadari Park', CurrentLocationLat: 1.3380, CurrentLocationLng: 103.8720,
    InterestedDistricts: '["D13"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-hyping'), tag('d-tag-fb')], EngagementActive: false,
    CreatedAt: iso(38), UpdatedAt: iso(6),
  },
  // --- Lost stage ---
  {
    Id: 'd-c29', CEANumber: 'DEMO', Name: 'Adrian Seah', Mobile: '+65 8901 1678', Type: 'investor',
    Budget: 5000000, PropertyType: 'Landed', PreferredLocation: 'Thomson', Stage: 'lost',
    Email: 'adrian.seah@example.com', HypingProjects: [hp('d-proj-3')],
    CurrentLocation: 'Sembawang Hills Estate', CurrentLocationLat: 1.3690, CurrentLocationLng: 103.8320,
    InterestedDistricts: '["D26"]', DistanceFromPreferredLocation: 3,
    Tags: [tag('d-tag-hyping')], EngagementActive: false,
    CreatedAt: iso(50), UpdatedAt: iso(12),
  },
  {
    Id: 'd-c30', CEANumber: 'DEMO', Name: 'Lakshmi Iyer', Mobile: '+65 8012 1789', Type: 'buyer',
    Budget: 1450000, PropertyType: 'HDB', PreferredLocation: 'Potong Pasir', Stage: 'lost',
    Email: 'lakshmi.iyer@example.com', HypingProjects: [hp('d-proj-1')],
    CurrentLocation: 'Potong Pasir Ave 1', CurrentLocationLat: 1.3310, CurrentLocationLng: 103.8690,
    InterestedDistricts: '["D13"]', DistanceFromPreferredLocation: 2,
    Tags: [tag('d-tag-eoi')], EngagementActive: false,
    CreatedAt: iso(55), UpdatedAt: iso(15),
  },
];

// Give enrolled demo clients a varied 7-day progress (deterministic by index)
// so the per-row day progress bar reads realistically in demo mode.
DEMO_CLIENTS.forEach((c, i) => {
  if (c.EngagementActive) {
    c.EngagementLastSentDayNumber = (i % 7) + 1;
    c.EngagementLastSentAt = iso(i % 3);
  }
});

const STAGE_ORDER = ['new', 'contacted', 'viewing', 'negotiation', 'won', 'lost'];

export function demoClientsResult(params?: { stage?: string; tags?: string[]; search?: string }): { clients: Client[]; total: number } {
  let list = [...DEMO_CLIENTS];
  if (params?.stage) list = list.filter((c) => c.Stage === params.stage);
  if (params?.tags && params.tags.length) {
    list = list.filter((c) => (c.Tags || []).some((t) => params.tags!.includes(String(t.Id))));
  }
  if (params?.search) {
    const q = params.search.toLowerCase();
    list = list.filter((c) => c.Name.toLowerCase().includes(q) || (c.Mobile || '').includes(q));
  }
  return { clients: list, total: list.length };
}

export function demoPipeline(): { pipeline: Record<string, Client[]>; stages: string[] } {
  const pipeline: Record<string, Client[]> = {};
  for (const s of STAGE_ORDER) pipeline[s] = DEMO_CLIENTS.filter((c) => c.Stage === s);
  return { pipeline, stages: STAGE_ORDER };
}

export function demoPipelineCounts(): { counts: Record<string, number> } {
  const counts: Record<string, number> = {};
  for (const s of STAGE_ORDER) counts[s] = DEMO_CLIENTS.filter((c) => c.Stage === s).length;
  return { counts };
}

export function demoDashboardSummary(): DashboardSummary {
  const stageCounts = STAGE_ORDER.map((s) => ({ stage: s, count: String(DEMO_CLIENTS.filter((c) => c.Stage === s).length) }));
  const typeCounts = ['buyer', 'investor'].map((t) => ({ type: t, count: String(DEMO_CLIENTS.filter((c) => c.Type === t).length) }));
  const clientTrend = Array.from({ length: 7 }, (_, i) => ({ date: iso(6 - i).slice(0, 10), count: String(1 + ((i * 2) % 4)) }));
  const waTrend = Array.from({ length: 7 }, (_, i) => ({ date: iso(6 - i).slice(0, 10), count: String(2 + ((i * 3) % 5)) }));
  const hypingProjectCounts = DEMO_HYPING_PROJECTS.map((p) => ({
    project: p.projectName,
    count: String(DEMO_CLIENTS.filter((c) => Array.isArray(c.HypingProjects) && c.HypingProjects.some((x) => x.name === p.projectName)).length),
  }));
  return {
    totalClients: DEMO_CLIENTS.length,
    totalWA: 84,
    stageCounts,
    typeCounts,
    clientTrend,
    waTrend,
    hypingProjectCounts,
  };
}

/** Today's 7-Day Engagement queue: a believable mid-morning snapshot. */
export function demoEngagementToday(): EngagementTodaySummary {
  const enrolled = DEMO_CLIENTS.filter((c) => c.EngagementActive);
  const statusFor = (i: number): 'pending' | 'sent' | 'failed' =>
    i % 5 === 3 ? 'pending' : i % 7 === 5 ? 'failed' : 'sent';
  const executions = enrolled.map((c, i) => {
    const status = statusFor(i);
    return {
      executionId: `d-exec-${i}`,
      dayNumber: c.EngagementLastSentDayNumber || 1,
      status,
      sentAt: status === 'sent' ? iso(0) : null,
      lastError: status === 'failed' ? 'WhatsApp bridge timeout — will retry next hour' : null,
      updatedAt: iso(0),
      clientId: c.Id,
      clientName: c.Name,
    };
  });
  const counts = { pending: 0, sending: 0, sent: 0, failed: 0, skipped: 0 };
  executions.forEach((e) => { counts[e.status as keyof typeof counts] += 1; });
  return { counts, executions };
}

export function demoAging(): AgingData {
  return {
    '0-3 days': DEMO_CLIENTS.slice(0, 2).map((c) => ({ Id: c.Id, Name: c.Name, Stage: c.Stage, daysSinceActivity: 1 })),
    '4-7 days': DEMO_CLIENTS.slice(2, 4).map((c) => ({ Id: c.Id, Name: c.Name, Stage: c.Stage, daysSinceActivity: 5 })),
    '8-14 days': DEMO_CLIENTS.slice(4, 6).map((c) => ({ Id: c.Id, Name: c.Name, Stage: c.Stage, daysSinceActivity: 10 })),
    '15+ days': DEMO_CLIENTS.slice(6, 8).map((c) => ({ Id: c.Id, Name: c.Name, Stage: c.Stage, daysSinceActivity: 20 })),
  };
}

export function demoWATypeDistribution(): Array<{ type: string; count: string }> {
  return [
    { type: 'Greeting', count: '28' },
    { type: 'Follow-up', count: '34' },
    { type: 'Listing', count: '22' },
  ];
}

// ─── Settings page fixtures ───
// The demo Settings page is fully browsable but read-only: these fixtures make
// it look like a live, configured account. Any edit/save routes through the
// write-blocking signedFetch wrapper, which surfaces the "register interest"
// prompt instead of persisting.

const DEMO_AGENT_PHONE = '+65 9123 4567';
const DEMO_VOICE_NOTE =
  'data:audio/wav;base64,UklGRkQDAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YSADAAAAAJYB/QIJBJsEoQQcBBoDugEmAI3+If0L/G37WfvT+8v8I/61/08BwQLgA4oEqwQ+BFED/wFxANb+Xf01/ID7Uvuz+5X83/1q/wYBgwK0A3UErwRcBIQDQgK8AB//nf1j/Jf7UPuX+2P8nf0f/7wAQgKEA1wErwR1BLQDgwIGAWr/3/2V/LP7UvuA+zX8Xf3W/nEA/wFRAz4EqwSKBOADwQJPAbX/I/7L/NP7Wftt+wv8If2N/iYAugEaAxwEoQSbBAkE/QKWAQAAav4D/ff7Zftf++T75vxG/tr/cwHfAvUDkwSnBC0ENQPdAUsAsf4//SD8dvtV+8L7r/wB/o//KgGjAssDgASuBE0EawMhApYA+v59/Uz8i/tR+6T7fPy+/UT/4QBjAp0DaQSwBGkEnQNjAuEARP++/Xz8pPtR+4v7TPx9/fr+lgAhAmsDTQSuBIAEywOjAioBj/8B/q/8wvtV+3b7IPw//bH+SwDdATUDLQSnBJME9QPfAnMB2v9G/ub85Ptf+2X79/sD/Wr+AACWAf0CCQSbBKEEHAQaA7oBJgCN/iH9C/xt+1n70/vL/CP+tf9PAcEC4AOKBKsEPgRRA/8BcQDW/l39NfyA+1L7s/uV/N/9av8GAYMCtAN1BK8EXASEA0ICvAAf/539Y/yX+1D7l/tj/J39H/+8AEIChANcBK8EdQS0A4MCBgFq/9/9lfyz+1L7gPs1/F391v5xAP8BUQM+BKsEigTgA8ECTwG1/yP+y/zT+1n7bfsL/CH9jf4mALoBGgMcBKEEmwQJBP0ClgEAAGr+A/33+2X7X/vk++b8Rv7a/3MB3wL1A5MEpwQtBDUD3QFLALH+P/0g/Hb7VfvC+6/8Af6P/yoBowLLA4AErgRNBGsDIQKWAPr+ff1M/Iv7Ufuk+3z8vv1E/+EAYwKdA2kEsARpBJ0DYwLhAET/vv18/KT7UfuL+0z8ff36/pYAIQJrA00ErgSABMsDowIqAY//Af6v/ML7Vft2+yD8P/2x/ksA3QE1Ay0EpwSTBPUD3wJzAdr/Rv7m/OT7X/tl+/f7A/1q/g==';

/** Sample WhatsApp message templates shown on the Message Templates tab. */
export function demoTemplates(): MessageTemplate[] {
  const mk = (id: string, title: string, type: string, body: string, daysOld: number): MessageTemplate => ({
    Id: id, CEANumber: 'DEMO', Title: title, Body: body, Type: type, Channel: 'whatsapp',
    CreatedAt: iso(daysOld), UpdatedAt: iso(Math.max(0, daysOld - 3)),
  });
  return [
    mk('d-tpl-1', 'Warm Intro', 'intro',
      'Hi {{client_name}}! This is {{agent_name}} 😊 Thanks for your interest in {{property_address}}. May I share a few units that fit what you\'re looking for?', 24),
    mk('d-tpl-2', 'Best Match', 'match',
      'Hi {{client_name}}, based on your budget and preferred area I\'ve shortlisted a strong match — here are the details: {{project_link}}. Shall I hold a slot for you?', 18),
    mk('d-tpl-3', 'Viewing Confirmation', 'viewing',
      'Hi {{client_name}}, confirming our viewing for {{property_address}}. See you there! Call me at {{agent_phone}} if anything changes.', 12),
    mk('d-tpl-4', 'Weekend Open House', 'marketing',
      'Hi {{client_name}}! We\'re holding an exclusive open house this weekend. Limited slots — reply YES and I\'ll reserve one for you. {{project_link}}', 6),
  ];
}

/** Connected, actively-running account state for the demo. */
export function demoAgentSettings(): AgentSettings {
  return {
    hasCalendlyKey: true,
    calendlyEventTypeUri: 'https://api.calendly.com/event_types/demo-property-consultation',
    calendlyUserUri: 'https://api.calendly.com/users/demo-agent',
    whatsAppStatus: 'connected',
    whatsAppPhoneNumber: DEMO_AGENT_PHONE,
    whatsAppPairedAt: iso(21),
    whatsAppProvider: 'bridge',
    engagementStartDayOffset: 1,
    engagementActive: true,
    engagementMessagesPerHour: 20,
    whatsAppCloudDailyBudgetSgd: null,
  };
}

export function demoCalendlyEventTypes(): CalendlyEventType[] {
  return [{
    uri: 'https://api.calendly.com/event_types/demo-property-consultation',
    name: 'Property Consultation',
    slug: 'property-consultation',
    scheduling_url: 'https://calendly.com/demo-agent/property-consultation',
    duration: 30,
    active: true,
    color: '#ed8936',
  }];
}

export function demoLatestPickerSalesUpdate(): LatestPickerSalesUpdate {
  return {
    imgUrl: '/distinctive/price-promo-edm.png',
    startDate: '2026-07-20',
    endDate: '2026-07-26',
  };
}

/** Live WhatsApp bridge status — paired and connected in the demo. */
export function demoWhatsAppStatus(): WhatsAppLiveStatus {
  return { status: 'connected', phone: DEMO_AGENT_PHONE, qrDataUrl: null };
}

/**
 * Official API looks connected in the demo. Sending stays on the paired bridge
 * so the 7-day sample does not switch to Meta template review. `templates` must
 * be an array — Settings calls `.find` on it during every render.
 */
export function demoWhatsAppCloudStatus(): WhatsAppCloudStatus {
  return {
    available: true,
    appId: null,
    configId: null,
    provider: 'bridge',
    account: {
      connected: true,
      status: 'connected',
      displayPhoneNumber: DEMO_AGENT_PHONE,
      verifiedName: 'Demo Agent',
      isCoexistence: true,
      paymentMethodLinked: true,
      paymentMethodVerifiable: true,
      qualityRating: 'GREEN',
      connectedAt: iso(21),
    },
    templates: [],
  };
}

/**
 * A fully configured 7-Day Engagement so every demo day is active and complete.
 * Only WhatsApp is populated because email is still a coming-soon integration.
 */
export function demoCampaigns(): { campaigns: Campaign[]; grouped: Record<string, Campaign[]> } {
  const mk = (day: number, body: string, extra?: Partial<Campaign>): Campaign => ({
    Id: `d-camp-${day}`, CEANumber: 'DEMO', Name: '7-Day Engagement', DayNumber: day,
    Channel: 'whatsapp', MessageTemplate: body, MediaUrl: null, CtaLabel: null, CtaUrl: null,
    IsActive: true, CreatedAt: iso(21), UpdatedAt: iso(5), ...extra,
  });
  const days: Campaign[] = [
    mk(1, 'Hi {{client_name}}! Great connecting with you. Over the next few days I\'ll share handpicked options and market insights for {{property_address}} 🏡'),
    mk(2, 'Good morning {{client_name}}! Wishing you a great day ahead ☀️ Here\'s a quick snapshot of what\'s moving in your preferred area.',
      { MediaUrl: goodDayImage }),
    mk(3, 'Hi {{client_name}}, I\'ve put together a PDF of nearby projects that match your preferences — take a look and let me know which ones catch your eye.'),
    mk(4, 'Hi {{client_name}}, here are this week\'s hottest-selling projects and the latest market activity worth watching.'),
    mk(5, '', { MediaUrl: DEMO_VOICE_NOTE }),
    mk(6, 'Hi {{client_name}}, would love to show you around in person. Pick a time that suits you and I\'ll take care of the rest 📅'),
    mk(7, 'Hi {{client_name}}, ready to take the next step? Tap below to explore the latest listings — I\'m here whenever you\'re ready.',
      {
        MediaUrl: '/distinctive/super-connector.jpeg',
        CtaLabel: 'View Listings',
        CtaUrl: 'https://www.huttonsgroup.com/',
      }),
  ];
  return { campaigns: days, grouped: { '7-Day Engagement': days } };
}

type DemoActivity = { Id: string; ClientId: string; Type: string; Description?: string | null; Date: string; CEANumber: string; CreatedAt: string };

/**
 * Stage-appropriate activity timeline per client: the further down the
 * pipeline, the longer the history. Deterministic (keyed off the client's
 * index) so revisiting a client shows the same story.
 */
function demoActivities(client: Client): DemoActivity[] {
  const idx = Math.max(0, DEMO_CLIENTS.findIndex((c) => c.Id === client.Id));
  const first = client.Name.split(' ')[0];
  const project = Array.isArray(client.HypingProjects) && client.HypingProjects.length
    ? client.HypingProjects[0].name : 'The Orie';
  const area = client.PreferredLocation || 'the area';
  const createdDaysAgo = Math.max(1, Math.round((now - Date.parse(client.CreatedAt)) / 86_400_000));

  // Every client starts the same way; each pipeline stage appends its chapter.
  const script: Array<{ type: string; desc: string }> = [
    { type: 'note', desc: `Added as a ${client.Type} lead — ${client.PropertyType || 'property'} budget around $${(client.Budget ?? 0).toLocaleString()}, prefers ${area}.` },
    { type: 'whatsapp', desc: `Sent intro message and ${project} brochure.` },
  ];
  // Lost clients stall at the contacted chapter before their closing note.
  const rank = client.Stage === 'lost' ? 1 : STAGE_ORDER.indexOf(client.Stage);
  if (rank >= 1) {
    script.push({ type: 'call', desc: `${first} responded — discussed budget and preferred districts.` });
    script.push({ type: 'whatsapp', desc: `Shared floor plans and the latest ${project} price list.` });
  }
  if (rank >= 2) {
    script.push({ type: 'viewing', desc: `Toured the ${project} show flat — liked the ${idx % 2 ? '3-bedder stack' : '2-bedder layout'}.` });
    script.push({ type: 'note', desc: 'Post-viewing debrief: keen, but comparing with nearby resale options.' });
  }
  if (rank >= 3) {
    script.push({ type: 'meeting', desc: 'Ran through pricing, progressive payment schedule and timeline.' });
    script.push({ type: 'call', desc: `Negotiating unit choice — eyeing ${idx % 2 ? 'a high-floor unit' : 'a pool-facing stack'}.` });
  }
  if (client.Stage === 'won') {
    script.push({ type: 'stage-change', desc: `OTP exercised at ${project} 🎉 Moved to Won.` });
    script.push({ type: 'note', desc: 'Sent congratulations and asked for referrals.' });
  }
  if (client.Stage === 'lost') {
    script.push({
      type: 'note',
      desc: idx % 2
        ? 'Decided to hold off until after bonus season — revisit next quarter.'
        : 'Went with a resale unit through another agent — keep warm for future launches.',
    });
  }

  // Spread the activities evenly from the day the client was added until now.
  const step = createdDaysAgo / script.length;
  return script.map((s, i) => {
    const when = iso(Math.max(0, Math.round(createdDaysAgo - step * (i + 1))));
    return { Id: `d-a-${client.Id}-${i}`, ClientId: client.Id, Type: s.type, Description: s.desc, Date: when, CEANumber: 'DEMO', CreatedAt: when };
  });
}

/**
 * Per-client 7-Day Engagement progress: days 1..N carry real executions, with
 * the latest day's status mirroring demoEngagementToday's queue (same
 * enrolled-index formula) so the dashboard and client detail agree.
 */
export function demoClientEngagement(id: string): ClientEngagementProgress {
  const client = DEMO_CLIENTS.find((c) => c.Id === id) || DEMO_CLIENTS[0];
  const campaign = demoCampaigns().campaigns;
  if (!client.EngagementActive) {
    return { EngagementActive: false, lastSentDay: null, lastSentAt: null, executions: [], campaign };
  }
  const progressDay = client.EngagementLastSentDayNumber || 1;
  const enrolledIdx = DEMO_CLIENTS.filter((c) => c.EngagementActive).findIndex((c) => c.Id === client.Id);
  const latestStatus: 'pending' | 'sent' | 'failed' =
    enrolledIdx % 5 === 3 ? 'pending' : enrolledIdx % 7 === 5 ? 'failed' : 'sent';
  const executions: CampaignExecution[] = Array.from({ length: progressDay }, (_, i) => {
    const day = i + 1;
    const status = day === progressDay ? latestStatus : 'sent';
    const when = iso(progressDay - day);
    return {
      Id: `d-ce-${client.Id}-${day}`, ClientId: client.Id, CampaignId: `d-camp-${day}`,
      DayNumber: day, SentAt: status === 'sent' ? when : null, Status: status,
      LastError: status === 'failed' ? 'WhatsApp bridge timeout — will retry next hour' : null,
      CreatedAt: when, UpdatedAt: when,
    };
  });
  const sentExecs = executions.filter((e) => e.Status === 'sent');
  const lastSent = sentExecs.length ? sentExecs[sentExecs.length - 1] : null;
  return {
    EngagementActive: true,
    lastSentDay: lastSent ? lastSent.DayNumber : 0,
    lastSentAt: lastSent?.SentAt ?? null,
    executions,
    campaign,
  };
}

export function demoClientDetail(id: string): {
  client: Client;
  preferences: [];
  activities: DemoActivity[];
  whatsappLogs: Array<{ Id: string; MessageBody: string; SentAt: string }>;
} {
  const client = DEMO_CLIENTS.find((c) => c.Id === id) || DEMO_CLIENTS[0];
  return {
    client,
    preferences: [],
    activities: demoActivities(client),
    whatsappLogs: [
      { Id: 'd-w1', MessageBody: `Hi ${client.Name.split(' ')[0]}, thanks for your interest! Here are a few units that match.`, SentAt: iso(2) },
      { Id: 'd-w2', MessageBody: 'Would you like to arrange a viewing this weekend?', SentAt: iso(1) },
    ],
  };
}
